import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, setCsrfProvider } from "@/lib/api";
import { ApiClientError, type SessionPayload } from "@/lib/types";

type SessionState = {
  session: SessionPayload | null;
  loading: boolean;
  error: string | null;
  status: "unknown" | "authenticated" | "unauthenticated" | "forbidden" | "error";
  refresh: () => Promise<SessionPayload | null>;
  applySession: (payload: SessionPayload) => void;
  clearSession: () => void;
  isOperator: boolean;
  isAdmin: boolean;
};

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<SessionState["status"]>("unknown");

  useEffect(() => {
    setCsrfProvider(() => session?.csrf_token ?? null);
  }, [session]);

  const applySession = useCallback((payload: SessionPayload) => {
    setSession(payload);
    setStatus("authenticated");
    setError(null);
  }, []);

  const clearSession = useCallback(() => {
    setSession(null);
    setStatus("unauthenticated");
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await api.session();
      applySession(payload);
      setLoading(false);
      return payload;
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 401) {
        clearSession();
        setError(null);
      } else if (e instanceof ApiClientError && e.status === 403) {
        setStatus("forbidden");
        setError(e.message);
      } else {
        setStatus("error");
        setError(e instanceof Error ? e.message : "Could not load session");
      }
      setLoading(false);
      return null;
    }
  }, [applySession, clearSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionState>(
    () => ({
      session,
      loading,
      error,
      status,
      refresh,
      applySession,
      clearSession,
      isOperator:
        session?.user.role === "operator" ||
        session?.user.role === "admin",
      isAdmin: session?.user.role === "admin",
    }),
    [session, loading, error, status, refresh, applySession, clearSession],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession requires SessionProvider");
  return ctx;
}
